import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Row, Spinner, Table } from "react-bootstrap";
import { apiErrorMessage, useGet, usePost } from "../../api";
import { EmptyState, PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { usePermissions } from "../../hooks";
import {
  IPartnerConfigurationApplyRequest,
  IPartnerConfigurationApplyResponse,
  IPartnerConfigurationResponse,
  IPartnerConfigurationValues,
  PARTNER_CONFIGURATION_ALL,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import {
  countryName,
  Drafts,
  FIELDS,
  formatValue,
  MergedRow,
  mergeRows,
  pendingChanges,
  pickValues,
  rowKeyOf,
  sameValues,
  templateValues,
  toStorageExplorerCsv,
} from "./configuration-model";
import { AddCountriesModal, BulkEditModal, ReviewChangesModal } from "./configuration-dialogs";
import { FieldInput } from "./field-input";

type Dialog = "add" | "bulk" | "review" | null;

const STATE_BADGE: Record<string, { bg: string; label: string } | undefined> = {
  new: { bg: "success", label: "New" },
  edited: { bg: "primary", label: "Edited" },
  removed: { bg: "danger", label: "Removing" },
};

const DIFF_STYLE = { background: "var(--bs-warning-bg-subtle)" };

export const PartnerConfiguration: React.FC = () => {
  const { can, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const canEdit = isAdmin || can(PermissionAction.UpdateAny, PermissionResource.Partners);
  const { data, loading, error, refetch } = useGet<IPartnerConfigurationResponse>("partners/configuration");
  const { execute: apply, loading: saving } = usePost<IPartnerConfigurationApplyResponse, IPartnerConfigurationApplyRequest>();

  const [drafts, setDrafts] = useState<Drafts>({});
  const [selectedPk, setSelectedPk] = useState<string | null>(null);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [showUnconfigured, setShowUnconfigured] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog>(null);
  const [saveResult, setSaveResult] = useState<IPartnerConfigurationApplyResponse | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const pending = useMemo(() => pendingChanges(rows, drafts), [rows, drafts]);

  const names = useMemo(() => new Map((data?.partners ?? []).map((p) => [p.partnerId.toLowerCase(), p.name])), [data]);
  const partnerLabel = (pk: string) => (pk === PARTNER_CONFIGURATION_ALL ? "All partners" : names.get(pk) ?? pk);

  const partnerEntries = useMemo(() => {
    const stats = new Map<string, { rows: number; pending: number }>();
    const bump = (pk: string, field: "rows" | "pending") => {
      const s = stats.get(pk) ?? { rows: 0, pending: 0 };
      s[field]++;
      stats.set(pk, s);
    };
    rows.forEach((r) => bump(r.partitionKey, "rows"));
    pending.forEach((p) => bump(p.change.partitionKey, "pending"));
    const pks = new Set<string>([PARTNER_CONFIGURATION_ALL, ...Array.from(names.keys()), ...Array.from(stats.keys())]);
    return Array.from(pks)
      .map((pk) => ({
        pk,
        name: partnerLabel(pk),
        known: pk === PARTNER_CONFIGURATION_ALL || names.has(pk),
        rows: stats.get(pk)?.rows ?? 0,
        pending: stats.get(pk)?.pending ?? 0,
      }))
      .sort((a, b) => {
        if (a.pk === PARTNER_CONFIGURATION_ALL) return -1;
        if (b.pk === PARTNER_CONFIGURATION_ALL) return 1;
        const ac = a.rows + a.pending > 0 ? 0 : 1;
        const bc = b.rows + b.pending > 0 ? 0 : 1;
        return ac - bc || a.name.localeCompare(b.name);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, pending, names]);

  const visiblePartners = partnerEntries.filter((p) => {
    const q = partnerSearch.trim().toLowerCase();
    if (q) return p.name.toLowerCase().includes(q) || p.pk.includes(q);
    return showUnconfigured || p.rows + p.pending > 0 || p.pk === selectedPk;
  });

  useEffect(() => {
    if (selectedPk || !partnerEntries.length) return;
    const first = partnerEntries.find((p) => p.pk !== PARTNER_CONFIGURATION_ALL && p.rows > 0) ?? partnerEntries[0];
    setSelectedPk(first.pk);
  }, [partnerEntries, selectedPk]);

  useEffect(() => {
    if (!pending.length) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [pending.length]);

  const merged = useMemo(() => (selectedPk ? mergeRows(rows, drafts, selectedPk) : []), [rows, drafts, selectedPk]);
  const allRow = merged.find((r) => r.rowKey === PARTNER_CONFIGURATION_ALL && r.state !== "removed");
  const globalAll = useMemo(
    () => mergeRows(rows, drafts, PARTNER_CONFIGURATION_ALL).find((r) => r.rowKey === PARTNER_CONFIGURATION_ALL && r.state !== "removed"),
    [rows, drafts]
  );
  const visibleRows = merged.filter((r) => {
    const q = countrySearch.trim().toLowerCase();
    return !q || r.rowKey.toLowerCase().includes(q) || countryName(r.rowKey).toLowerCase().includes(q);
  });
  const selectedVisible = visibleRows.filter((r) => selectedRows.has(r.key));

  const selectPartner = (pk: string) => {
    setSelectedPk(pk);
    setSelectedRows(new Set());
    setCountrySearch("");
  };

  // A draft keeps the etag it started from, so later edits and refreshes can't silently rebase it.
  const startEtag = (d: Drafts, row: MergedRow) => (row.key in d ? d[row.key].etag : row.server?.etag ?? null);

  const setRowValues = (row: MergedRow, values: IPartnerConfigurationValues) =>
    setDrafts((d) => {
      const next = { ...d };
      const etag = startEtag(d, row);
      if (row.server && etag === row.server.etag && sameValues(values, pickValues(row.server))) delete next[row.key];
      else next[row.key] = { partitionKey: row.partitionKey, rowKey: row.rowKey, values, etag };
      return next;
    });

  const removeRows = (targets: MergedRow[]) =>
    setDrafts((d) => {
      const next = { ...d };
      for (const r of targets) {
        const etag = startEtag(d, r);
        if (etag) next[r.key] = { partitionKey: r.partitionKey, rowKey: r.rowKey, values: null, etag };
        else delete next[r.key];
      }
      return next;
    });

  // The user has seen the row's current values and wants their edit applied on top of them.
  const keepMine = (row: MergedRow) =>
    setDrafts((d) => {
      const draft = d[row.key];
      if (!draft) return d;
      const next = { ...d };
      const etag = row.server?.etag ?? null;
      if (draft.values === null && !row.server) delete next[row.key];
      else if (row.server && draft.values && sameValues(draft.values, pickValues(row.server))) delete next[row.key];
      else next[row.key] = { ...draft, etag };
      return next;
    });

  const revertRows = (keys: string[]) =>
    setDrafts((d) => {
      const next = { ...d };
      keys.forEach((k) => delete next[k]);
      return next;
    });

  const addRows = (rowKeys: string[], values: IPartnerConfigurationValues) => {
    if (!selectedPk) return;
    setDrafts((d) => {
      const next = { ...d };
      for (const rk of rowKeys) next[rowKeyOf(selectedPk, rk)] = { partitionKey: selectedPk, rowKey: rk, values, etag: null };
      return next;
    });
    setDialog(null);
  };

  const bulkApply = (fields: Array<keyof IPartnerConfigurationValues>, values: IPartnerConfigurationValues) => {
    for (const r of selectedVisible) {
      if (r.state === "removed") continue;
      const next = { ...r.values };
      for (const f of fields) (next as Record<string, unknown>)[f] = values[f];
      setRowValues(r, next);
    }
    setDialog(null);
  };

  const save = async () => {
    setSaveError(null);
    try {
      const result = await apply("partners/configuration/apply", { changes: pending.map((p) => p.change) });
      const done = result.results.filter((r) => r.ok).map((r) => rowKeyOf(r.partitionKey, r.rowKey));
      revertRows(done);
      setSaveResult(result);
      refetch().catch(() => undefined);
    } catch (err) {
      setSaveError(apiErrorMessage(err));
    }
  };

  const downloadCsv = () => {
    const blob = new Blob([toStorageExplorerCsv(rows)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${data?.table ?? "PartnerConfiguration"}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const toggleRow = (key: string) =>
    setSelectedRows((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedRows.has(r.key));

  const fallbackText = !selectedPk
    ? ""
    : selectedPk === PARTNER_CONFIGURATION_ALL
      ? "These rows apply to every partner that has no row of its own for the country. A partner's own rows always win."
      : allRow
        ? "Countries without their own row use this partner's ALL row. Highlighted cells differ from it."
        : globalAll
          ? "This partner has no ALL row, so countries without their own row use the all-partners default row."
          : "This partner has no ALL row and there is no all-partners default, so countries without their own row get Platform defaults.";

  return (
    <div>
      <PageHeader
        title="Partner configuration"
        description="What checkout and Open Data return for each partner app, by country: coupon enforcement, offer display, caps and codeless deals. Edits stay in a draft until every change is reviewed and confirmed."
      />
      {data && !data.configured && (
        <EmptyState
          title="Partner configuration isn't available here"
          message="This environment isn't connected to the partner configuration table, so it can't be viewed or edited from here."
          icon="bi-plug"
        />
      )}
      {data?.configured && (
      <PageToolbar>
        {canEdit && (
          <ToolbarButton
            label="Review & save"
            icon="bi-check2-square"
            variant="primary"
            count={pending.length}
            disabled={!pending.length}
            onClick={() => {
              setSaveResult(null);
              setSaveError(null);
              setDialog("review");
            }}
            hint="See every pending change, confirm each one, then write them to the table"
          />
        )}
        {canEdit && (
          <ToolbarButton
            label="Discard draft"
            icon="bi-x-circle"
            disabled={!pending.length}
            onClick={() => window.confirm(`Discard ${pending.length} unsaved changes?`) && setDrafts({})}
            hint="Throw away every unsaved change across all partners"
          />
        )}
        <ToolbarButton label="Refresh" icon="bi-arrow-clockwise" onClick={() => refetch().catch(() => undefined)} hint="Reload the table; your unsaved draft is kept" />
        <ToolbarButton label="History" icon="bi-clock-history" to="/partners/configuration/history" hint="Who changed what, plus full copies of the table taken before every save" />
        <ToolbarButton label="Download CSV" icon="bi-download" disabled={!rows.length} onClick={downloadCsv} hint="The table as saved, in Storage Explorer's import format" />
      </PageToolbar>
      )}

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {data && data.configured && (
        <>
          <div className="small text-muted mb-2">
            Table <code>{data.table}</code> · {rows.length} rows · loaded {new Date(data.loadedAt).toLocaleTimeString()}
          </div>
          <Row className="g-3">
            <Col lg={3}>
              <Card>
                <Card.Body className="p-2">
                  <Form.Control size="sm" placeholder="Search partner name or id" value={partnerSearch} onChange={(e) => setPartnerSearch(e.target.value)} className="mb-2" />
                  <Form.Check
                    type="switch"
                    id="show-unconfigured"
                    className="small mb-2"
                    label="Show partners without configuration"
                    checked={showUnconfigured}
                    onChange={(e) => setShowUnconfigured(e.target.checked)}
                  />
                </Card.Body>
                <ListGroup variant="flush" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  {visiblePartners.map((p) => (
                    <ListGroup.Item key={p.pk} action active={p.pk === selectedPk} onClick={() => selectPartner(p.pk)} className="py-2">
                      <div className="d-flex justify-content-between align-items-center gap-2">
                        <span className="small text-truncate" title={p.pk}>
                          {p.pk === PARTNER_CONFIGURATION_ALL ? <em>All partners (defaults)</em> : p.name}
                        </span>
                        <span className="d-flex gap-1">
                          {p.pending > 0 && <Badge bg="warning" text="dark" title="Unsaved changes">{p.pending}</Badge>}
                          {p.rows > 0 ? <Badge bg="secondary" title="Rows in the table">{p.rows}</Badge> : <span className="small text-muted">none</span>}
                        </span>
                      </div>
                      {!p.known && <div className="small text-danger">Not in the partners database</div>}
                    </ListGroup.Item>
                  ))}
                  {!visiblePartners.length && <ListGroup.Item className="small text-muted">No partner matches.</ListGroup.Item>}
                </ListGroup>
              </Card>
            </Col>

            <Col lg={9}>
              {selectedPk && (
                <Card>
                  <Card.Body>
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                      <div>
                        <h5 className="mb-0">{partnerLabel(selectedPk)}</h5>
                        {selectedPk !== PARTNER_CONFIGURATION_ALL && <div className="small text-muted font-monospace">{selectedPk}</div>}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {canEdit && (
                          <Button size="sm" onClick={() => setDialog("add")} title="Give one or more countries (or ALL) their own settings">
                            Add countries
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline-primary" disabled={!selectedVisible.length} onClick={() => setDialog("bulk")} title="Set the same values on every ticked row">
                            Edit selected ({selectedVisible.length})
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline-danger" disabled={!selectedVisible.length} onClick={() => removeRows(selectedVisible)} title="Mark the ticked rows for removal; they fall back to ALL">
                            Remove selected
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => navigate(`/partners/configuration/history?partner=${encodeURIComponent(selectedPk)}`)}
                          title="Changes saved from this panel for this partner"
                        >
                          History
                        </Button>
                      </div>
                    </div>
                    <p className="small text-muted mb-2">{fallbackText}</p>
                    <Form.Control size="sm" placeholder="Filter countries" value={countrySearch} onChange={(e) => setCountrySearch(e.target.value)} className="mb-2" style={{ maxWidth: 260 }} />

                    {!merged.length ? (
                      <EmptyState
                        title="No configuration for this partner"
                        message={canEdit ? "Add an ALL row to cover every country, or add specific countries." : "This partner uses the defaults."}
                        icon="bi-sliders"
                      />
                    ) : (
                      <div style={{ maxHeight: "65vh", overflow: "auto" }}>
                        <Table size="sm" hover className="align-middle small mb-0">
                          <thead className="sticky-top bg-body">
                            <tr>
                              <th>
                                <Form.Check
                                  type="checkbox"
                                  checked={allVisibleSelected}
                                  onChange={() => setSelectedRows(allVisibleSelected ? new Set() : new Set(visibleRows.map((r) => r.key)))}
                                  title="Tick every row shown"
                                />
                              </th>
                              <th>Country</th>
                              {FIELDS.map((f) => (
                                <th key={f.key} title={f.hint} className="text-nowrap">
                                  {f.label}
                                </th>
                              ))}
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRows.map((r) => {
                              const removed = r.state === "removed";
                              const badge = STATE_BADGE[r.state];
                              return (
                                <tr key={r.key} className={removed ? "opacity-50" : ""}>
                                  <td>
                                    <Form.Check type="checkbox" checked={selectedRows.has(r.key)} onChange={() => toggleRow(r.key)} />
                                  </td>
                                  <td className="text-nowrap">
                                    <strong>{r.rowKey}</strong> <span className="text-muted">{countryName(r.rowKey)}</span>
                                    {badge && (
                                      <Badge bg={badge.bg} className="ms-1">
                                        {badge.label}
                                      </Badge>
                                    )}
                                    {r.stale && (
                                      <Badge
                                        bg="warning"
                                        text="dark"
                                        className="ms-1"
                                        title={r.server ? `Someone else saved this row after you started editing. Current: ${FIELDS.map((f) => `${f.label} ${formatValue(f.key, r.server![f.key])}`).join(", ")}` : "Someone else removed this row after you started editing."}
                                      >
                                        Changed by someone else
                                      </Badge>
                                    )}
                                  </td>
                                  {FIELDS.map((f) => {
                                    const differs = !!allRow && r.key !== allRow.key && allRow.values[f.key] !== r.values[f.key];
                                    return (
                                      <td key={f.key} style={differs ? DIFF_STYLE : undefined} title={differs ? `ALL row: ${formatValue(f.key, allRow!.values[f.key])}` : undefined}>
                                        {canEdit && !removed ? (
                                          <FieldInput field={f.key} values={r.values} onChange={(v) => setRowValues(r, v)} compact />
                                        ) : (
                                          formatValue(f.key, r.values[f.key])
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="text-end text-nowrap">
                                    {canEdit && r.state === "clean" && (
                                      <Button size="sm" variant="link" className="text-danger p-0" onClick={() => removeRows([r])} title="Mark this row for removal">
                                        Remove
                                      </Button>
                                    )}
                                    {canEdit && r.stale && (
                                      <Button size="sm" variant="link" className="p-0 me-2" onClick={() => keepMine(r)} title="Apply your edit on top of the row as it is now">
                                        Keep mine
                                      </Button>
                                    )}
                                    {canEdit && r.state !== "clean" && (
                                      <Button size="sm" variant="link" className="p-0" onClick={() => revertRows([r.key])} title="Put this row back as it is in the table">
                                        {r.state === "new" ? "Discard" : r.state === "removed" ? "Keep" : "Revert"}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </>
      )}

      {dialog === "add" && selectedPk && (
        <AddCountriesModal
          partnerLabel={partnerLabel(selectedPk)}
          existing={new Set(merged.filter((r) => r.state !== "removed").map((r) => r.rowKey))}
          template={templateValues(rows, drafts, selectedPk)}
          onAdd={addRows}
          onHide={() => setDialog(null)}
        />
      )}
      {dialog === "bulk" && selectedVisible.length > 0 && (
        <BulkEditModal count={selectedVisible.length} initial={selectedVisible[0].values} onApply={bulkApply} onHide={() => setDialog(null)} />
      )}
      {dialog === "review" && (
        <ReviewChangesModal
          changes={pending}
          partnerLabel={partnerLabel}
          saving={saving}
          result={saveResult}
          error={saveError}
          onSave={save}
          onHide={() => setDialog(null)}
        />
      )}
    </div>
  );
};
